/** @jsx isolateComponent */
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { css } from '@emotion/react';
import { CTATextContent } from '../CTATextContent';
import { SubscriptionFormWithDisclaimer } from '../../../forms/SubscriptionForm';
import { BgColor, DeprecatedBgColor, CTABtnColor } from '../types';
import { getBgColor, getCtaBtnColor } from '../utils';
import {
  Button,
  BrandButton,
  DarkBackgroundButton,
  Colors,
  ButtonProps,
} from '../../../../index';
import type { AnchorTarget } from '../../../../ts-types';

export type Props = {
  /**
   * Sets the container's background color, must be one of type `'lava60' | 'ocean40' | 'sun60' | 'white'
   * | 'zapier' | 'night' | 'moss' | 'neutral800' | 'neutral100' | 'earth'`.
   */
  backgroundColor?: BgColor | DeprecatedBgColor;
  /**
   * max-width in pixels applied to the content area of container.
   */
  maxWidth?: number;
  /**
   * Heading text content.
   */
  heading: React.ReactNode;
  /**
   * Text content displayed below the heading.
   */
  subHeading?: React.ReactNode;
  /**
   * Text content displayed below the sub-heading.
   */
  description?: React.ReactNode;
  /**
   * Text content of the CTA button.
   */
  ctaText?: React.ReactNode;
  /**
   * href for the CTA button.
   */
  ctaHref?: string;
  /**
   * Color of the CTA button.
   *
   * Must be one of the type `'primary' | 'primary-alt' | 'primary-dark' | 'brand'`
   */
  ctaBtnColor?: CTABtnColor;
  /**
   * CTA button click handler.
   */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**
   * CTA button keyDown handler.
   */
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  /**
   * Whether the subscription form is enabled in place of CTA button.
   */
  isSubscriptionForm?: boolean;
  /**
   * Placeholder text to display inside the subscription form input field.
   */
  subscriptionPlaceholder?: string;
  /**
   * Function that gets called on subscription form submit.
   */
  onSubscriptionFormSubmit?: (subscriptionValue?: string) => void;
  /**
   * The `target` attribute for the CTA button.
   */
  ctaTarget?: AnchorTarget;
};

const Styles = labelStyles('CTACentered', {
  root: (backgroundColor: DeprecatedBgColor | BgColor) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 30px;
    background-color: ${Colors[backgroundColor]};

    @media screen and (min-width: 768px) {
      padding: 40px;
    }

    @media screen and (min-width: 1230px) {
      padding: 70px 200px;
    }
  `,
  mediaContent: (maxWidth: number) => css`
    max-width: ${maxWidth}px;
    @media screen and (min-width: 768px) {
      margin: 0 auto;
    }
  `,
});

const getSubscriptionFormButton = (
  bgColor: BgColor | DeprecatedBgColor,
  ctaBtnColor?: CTABtnColor
) => {
  const btnColor = getCtaBtnColor(bgColor, ctaBtnColor);
  const btnProps = {
    children: 'Submit',
    type: 'submit' as ButtonProps['type'],
  };
  if (btnColor === 'primary-dark')
    return <DarkBackgroundButton {...btnProps} />;
  else if (btnColor === 'brand') return <BrandButton {...btnProps} />;
  else {
    return <Button {...btnProps} color={btnColor} size="small" />;
  }
};

export const CTACentered = ({
  backgroundColor = 'neutral100',
  ctaHref,
  ctaBtnColor,
  ctaText,
  description,
  heading,
  maxWidth = 570,
  onCtaClick,
  onCtaKeyDown,
  subHeading,
  isSubscriptionForm,
  subscriptionPlaceholder,
  onSubscriptionFormSubmit,
  ctaTarget,
}: Props) => {
  const bgColor = getBgColor(backgroundColor);

  const isZapierOrange = bgColor === 'zapier';

  if (isZapierOrange && isSubscriptionForm) {
    console.error(
      'Do not use the subscription form with the zapier background. The disclaimer text accessibility ' +
        'is poor on the zapier background.'
    );
  }

  return (
    <div css={Styles.root(bgColor)}>
      <div css={Styles.mediaContent(maxWidth)}>
        <CTATextContent
          heading={heading}
          subHeading={subHeading}
          description={description}
          ctaText={!isSubscriptionForm ? ctaText : undefined}
          ctaHref={!isSubscriptionForm ? ctaHref : undefined}
          ctaBtnColor={ctaBtnColor}
          onCtaClick={!isSubscriptionForm ? onCtaClick : undefined}
          onCtaKeyDown={onCtaKeyDown}
          bgColor={bgColor}
          alignment="center"
          ctaTarget={ctaTarget}
        />
      </div>
      {isSubscriptionForm ? (
        <SubscriptionFormWithDisclaimer
          placeholderText={subscriptionPlaceholder || ''}
          renderButton={() =>
            getSubscriptionFormButton(backgroundColor, ctaBtnColor)
          }
          onSubmit={onSubscriptionFormSubmit}
          disclaimerTextColor={
            bgColor === 'neutral100' ? 'primary' : 'secondary'
          }
          disclaimerTextType="paragraph3"
        />
      ) : null}
    </div>
  );
};
